import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  Tooltip as RechartsTooltip,
  XAxis,
  type XAxisTickContentProps,
  YAxis
} from 'recharts';
import { Card, CardContent, CardHeader } from '../../elements/ui/card';
import { Skeleton } from '../../elements/ui/skeleton';
import { ChartContainer, type ChartConfig } from '../../elements/ui/chart';
import { ChartHoverCard } from '../../elements/ui/hover-card';
import { CHART_GROW_MS, useFirstDrawAnimation, useRevealOnScroll } from '../../hooks/useMotion';
import { busyDimClass } from '../../elements/ui/motion';
import {
  PigletRegistrationCycle,
  PigletRegistrationCycleDetail,
  PigletRegistrationDashboard,
  cycleWeekSpan,
  isCycleBelowExpected,
  summariseCycle
} from '../../models/piglet-registration';
import { pigletRegistrationColors as c } from './colors';

interface ChartRow {
  key: string;
  cycle: PigletRegistrationCycle;
  belowExpected: boolean;
  /** Registered per week of the cycle, bottom segment first. */
  segments: number[];
  /** The in-progress cycle's gap up to its expected volume, drawn as a dotted outline. Zero otherwise. */
  remaining: number;
  /** Where the total sits: on top of the bar, or on top of the outline for the cycle in progress. */
  labelY: number;
  totalLabel: string;
  rangeLabel: string;
  /** The weight and mortality detail of the cycle's weeks, or null when none of them registered anything. */
  detail: PigletRegistrationCycleDetail | null;
}

/** Most x-axis labels drawn before they are thinned out. Each one is two lines wide enough for "w40–41". */
const MAX_TICKS = 14;

const num = (value: number) => value.toLocaleString();
const pct = (value: number) => `${Math.round(value)}%`;

const chartConfig: ChartConfig = {
  registered: { label: 'Registered', color: c.registered },
  expected: { label: 'Expected', color: c.expected }
};

/** The ramp step for one segment. A cycle longer than the ramp keeps its lightest step for the extra weeks. */
const rampStep = (ramp: string[], index: number) => ramp[Math.min(index, ramp.length - 1)];

/** One label/value row inside the hover card. */
const TooltipRow = ({ label, value, dotColor }: { label: string; value: string; dotColor?: string }) => (
  <div className="flex items-center justify-between gap-6">
    <span className="flex items-center gap-1.5 text-gray-500">
      {dotColor && <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />}
      {label}
    </span>
    <span className="font-medium tabular-nums text-gray-900">{value}</span>
  </div>
);

interface CycleTooltipProps {
  active?: boolean;
  payload?: { payload: ChartRow }[];
  /** True when every cycle is a single week, so the card reads like the weekly one it replaces. */
  weekly: boolean;
}

/**
 * ChartHoverCard rather than a plain div: this card is nearly as tall as the plot area, and once it is
 * taller - one more weight class, a longer translation, browser zoom - recharts stops clamping it and the
 * card grows the page's scroll height instead. See the note on ChartHoverCard.
 *
 * The weight and mortality detail is rolled up over the cycle's weeks rather than listed per week, so a
 * 4-week cycle does not turn the card into a table.
 */
function CycleTooltip({ active, payload, weekly }: CycleTooltipProps) {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const { cycle, belowExpected, detail } = payload[0].payload;
  const span = cycleWeekSpan(cycle);
  const percent = Math.round(cycle.percentOfExpected);

  const verdict = !cycle.isComplete
    ? t('f-piglets-reg-verdict-running', { registered: num(cycle.registered), expected: num(cycle.expected) })
    : belowExpected
      ? t('f-piglets-reg-verdict-below', { percent })
      : t('f-piglets-reg-verdict-on-track', { percent });

  return (
    <ChartHoverCard className="min-w-56">
      <p className="mb-1.5 font-medium text-gray-700">
        {span.first === span.last
          ? t('f-piglets-reg-tooltip-week', { week: span.first })
          : t('f-piglets-reg-tooltip-cycle', { from: span.first, to: span.last })}
      </p>

      <div className="grid gap-1">
        {cycle.weeks.length > 1 ? (
          <>
            {cycle.weeks.map((week, index) => (
              <TooltipRow
                key={`${week.year}-${week.weekNumber}`}
                label={t('f-piglets-reg-tooltip-cycle-week', { week: week.weekNumber, position: index + 1 })}
                value={week.isFuture ? t('f-piglets-reg-tooltip-not-started') : num(week.registered)}
                dotColor={
                  cycle.isComplete ? rampStep(belowExpected ? c.belowExpectedRamp : c.cycleWeekRamp, index) : undefined
                }
              />
            ))}
            <TooltipRow label={t('f-piglets-reg-tooltip-cycle-total')} value={num(cycle.registered)} />
          </>
        ) : (
          <TooltipRow label={t('f-piglets-reg-legend-registered')} value={num(cycle.registered)} />
        )}
        <TooltipRow
          label={t(weekly ? 'f-piglets-reg-legend-expected' : 'f-piglets-reg-legend-expected-cycle')}
          value={num(cycle.expected)}
        />
      </div>

      {/* A schedule change can end a cycle early; its expected volume is already scaled down to match, and
          without this the smaller figure would look like a different target. */}
      {cycle.weeks.length < cycle.cycleWeeks && (
        <p className="mt-1.5 max-w-64 text-gray-500">{t('f-piglets-reg-tooltip-cut-short')}</p>
      )}

      <p className="mt-2 border-t border-gray-100 pt-2 text-gray-700">{verdict}</p>

      {detail && (
        <>
          <div className="mt-2 grid gap-1 border-t border-gray-100 pt-2">
            <TooltipRow
              label={t('f-piglets-reg-birth-weight')}
              value={t('f-piglets-reg-grams', { value: num(detail.averageBirthWeightGrams) })}
            />
            <TooltipRow
              label={t('f-piglets-reg-tooltip-mortality')}
              value={t('f-piglets-reg-tooltip-mortality-value', {
                percent: Math.round(detail.mortalityPercent),
                dead: num(detail.deadCount)
              })}
            />
          </div>

          <p className="mt-2 mb-1 border-t border-gray-100 pt-2 font-medium text-gray-700">
            {/* Shares of the classified piglets, not of everything registered - the API is explicit about this. */}
            {t('f-piglets-reg-tooltip-weight-split', { classified: num(detail.classifiedCount) })}
          </p>

          <div className="grid gap-1">
            <TooltipRow
              label={t('f-piglets-reg-class-light')}
              value={pct(detail.lightPercent)}
              dotColor={c.weightClass.light}
            />
            <TooltipRow
              label={t('f-piglets-reg-class-medium')}
              value={pct(detail.mediumPercent)}
              dotColor={c.weightClass.medium}
            />
            <TooltipRow
              label={t('f-piglets-reg-class-heavy')}
              value={pct(detail.heavyPercent)}
              dotColor={c.weightClass.heavy}
            />
          </div>
        </>
      )}
    </ChartHoverCard>
  );
}

/** A small swatch drawn with the same stripes as the in-progress bar. */
const hatchSwatchStyle: React.CSSProperties = {
  backgroundImage: `repeating-linear-gradient(45deg, ${c.inProgress.stripe} 0 2px, ${c.inProgress.ground} 2px 5px)`
};

/**
 * Legend is hand-rolled so the expected series can show as a dash rather than a filled swatch. With
 * multi-week cycles each ramp step gets its own entry, since the shade is what tells cycle week 1 from 2.
 */
function ChartLegendRow({ segmentCount }: { segmentCount: number }) {
  const { t } = useTranslation();
  const weekly = segmentCount === 1;

  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
      {weekly ? (
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c.registered }} />
          {t('f-piglets-reg-legend-registered')}
        </span>
      ) : (
        Array.from({ length: segmentCount }, (_, index) => (
          <span key={index} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: rampStep(c.cycleWeekRamp, index) }} />
            {t('f-piglets-reg-legend-cycle-week', { week: index + 1 })}
          </span>
        ))
      )}
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c.belowExpected }} />
        {t('f-piglets-reg-legend-below-cycle')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[2px]" style={hatchSwatchStyle} />
        {t('f-piglets-reg-legend-in-progress')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-4 border-t-2 border-dashed" style={{ borderColor: c.expected }} />
        {t(weekly ? 'f-piglets-reg-legend-expected' : 'f-piglets-reg-legend-expected-cycle')}
      </span>
    </div>
  );
}

/**
 * Two lines under each bar: the weeks the cycle covers, and its verdict - the share of expected for a
 * completed cycle, "running" for the one in progress. The verdict sits on the axis rather than only in the
 * tooltip so a red bar can be read without hovering it.
 */
const CycleTick = ({ x, y, payload, rows }: XAxisTickContentProps & { rows: ChartRow[] }) => {
  const { t } = useTranslation();
  const row = rows[payload.index];

  if (!row) return null;

  const { cycle, belowExpected } = row;

  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={12} textAnchor="middle" fontSize={12} fill="#6b7280">
        {row.rangeLabel}
      </text>
      <text
        dy={27}
        textAnchor="middle"
        fontSize={11}
        fill={belowExpected ? c.belowExpectedRamp[0] : '#9ca3af'}
        fontWeight={belowExpected ? 700 : 400}
      >
        {!cycle.isComplete
          ? t('f-piglets-reg-cycle-running')
          : `${belowExpected ? '▼ ' : ''}${Math.round(cycle.percentOfExpected)}%`}
      </text>
    </g>
  );
};

/**
 * Recharts' automatic domain produces ticks like 65 / 130 / 195 once the expected line stretches the axis.
 * Rounding the top up to a 1/2/2.5/5 x 10^n step gets back to countable gridlines (0 / 100 / 200 / 300).
 */
const buildYAxisScale = (maxValue: number): { max: number; ticks: number[] } => {
  if (maxValue <= 0) return { max: 10, ticks: [0, 5, 10] };

  const targetIntervals = 4;
  const rawStep = maxValue / targetIntervals;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= rawStep) ?? 10 * magnitude;
  const max = Math.ceil(maxValue / step) * step;

  const ticks: number[] = [];
  for (let value = 0; value <= max + step / 2; value += step) {
    ticks.push(Math.round(value));
  }

  return { max, ticks };
};

const ChartSkeleton = () => (
  <div className="flex h-full w-full items-end gap-2 px-4 pb-6">
    {[55, 75, 45, 85, 65, 90, 50, 70, 80, 60, 40, 72].map((height, i) => (
      <Skeleton key={i} className="flex-1 rounded-t-sm rounded-b-none" style={{ height: `${height}%` }} />
    ))}
  </div>
);

export interface RegisteredPigletsChartProps {
  dashboard?: PigletRegistrationDashboard | null;
  isBusy: boolean;
}

/**
 * One bar per production cycle, stacked by the cycle's weeks, against one expected-per-cycle line.
 *
 * The cycle is what is judged, because that is what the expected volume is set for: on a 2-week batch
 * system a quiet week followed by a busy one is the schedule working, not a farm falling behind. A week is
 * therefore never coloured on its own - only a completed cycle under 80% turns red, and the cycle in
 * progress is hatched and outlined up to its target instead of being measured against it half-way through.
 */
export default function RegisteredPigletsChart({ dashboard, isBusy }: RegisteredPigletsChartProps) {
  const { t } = useTranslation();
  // useId's colons are not safe inside url(#...), so they are stripped from the pattern id.
  const hatchId = `piglet-cycle-hatch-${React.useId().replace(/:/g, '')}`;

  const cycles = dashboard?.cycles ?? [];
  const weeks = dashboard?.weeks ?? [];
  const expectedPerCycle = dashboard?.expectedPerCycle ?? 0;

  // Sized from the cycles themselves rather than from `cycleWeeks`: the schedule can have changed inside the
  // period, and every week of the longest cycle needs a segment to land in.
  const segmentCount = Math.max(1, dashboard?.cycleWeeks ?? 1, ...cycles.map((cycle) => cycle.weeks.length));
  const weekly = segmentCount === 1;

  const rows: ChartRow[] = cycles.map((cycle) => {
    const span = cycleWeekSpan(cycle);
    const remaining = cycle.isComplete ? 0 : Math.max(0, cycle.expected - cycle.registered);

    return {
      key: cycle.startDate,
      cycle,
      belowExpected: isCycleBelowExpected(cycle),
      segments: cycle.weeks.map((week) => week.registered),
      remaining,
      labelY: cycle.registered + remaining,
      totalLabel: cycle.isComplete
        ? num(cycle.registered)
        : t('f-piglets-reg-so-far', { value: num(cycle.registered) }),
      rangeLabel:
        span.first === span.last
          ? t('f-piglets-reg-week-short', { week: span.first })
          : t('f-piglets-reg-week-range', { from: span.first, to: span.last }),
      detail: summariseCycle(cycle, weeks)
    };
  });

  const segmentFill = (row: ChartRow, index: number) => {
    if (!row.cycle.isComplete) return `url(#${hatchId})`;
    if (weekly) return row.belowExpected ? c.belowExpected : c.registered;
    return rampStep(row.belowExpected ? c.belowExpectedRamp : c.cycleWeekRamp, index);
  };

  // Thinned by hand: recharts would measure each tick by its value - the cycle's start date - rather than by
  // the two short lines CycleTick draws, and hide far more labels than it needs to. A 52-week weekly period
  // keeps every fourth one; the hover card still has every cycle's verdict.
  const tickInterval = Math.max(0, Math.ceil(rows.length / MAX_TICKS) - 1);

  // The expected line is usually the tallest thing on the chart, so it has to be part of the domain.
  const yScale = buildYAxisScale(Math.max(0, expectedPerCycle, ...rows.map((row) => row.labelY)));

  // Drawn once it scrolls into view, so the bars grow in where the reader can see them. A reload keeps the
  // previous chart on screen, dimmed, rather than dropping back to the skeleton.
  const { ref: plotRef, revealed } = useRevealOnScroll();
  const animateFirstDraw = useFirstDrawAnimation(revealed && rows.length > 0);

  return (
    <Card>
      <CardHeader className="space-y-1 pb-4">
        <h3 className="text-base font-semibold text-gray-900">
          {t(weekly ? 'f-piglets-reg-chart-title' : 'f-piglets-reg-chart-title-cycle')}
        </h3>
        <p className="text-xs text-gray-500">{t('f-piglets-reg-chart-subtitle-cycle')}</p>
        {dashboard && !dashboard.hasProductionSchedule && (
          <p className="text-xs text-gray-400">{t('f-piglets-reg-no-schedule')}</p>
        )}
        <div className="pt-2">
          <ChartLegendRow segmentCount={segmentCount} />
        </div>
      </CardHeader>

      <CardContent>
        <div ref={plotRef} className="h-80 w-full">
          {isBusy && rows.length === 0 ? (
            <ChartSkeleton />
          ) : rows.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              {t('f-piglets-reg-empty')}
            </div>
          ) : !revealed ? null : (
            <ChartContainer
              config={chartConfig}
              resizeDebounce={250}
              className={`h-full w-full ${busyDimClass(isBusy)}`}
            >
              {/* The right gutter holds the "expected N" annotation, which sits beside the line rather than
                  on top of it so it can never collide with a tall bar's value label. */}
              <ComposedChart
                data={rows}
                margin={{ top: 24, right: weekly ? 76 : 132, bottom: 8, left: 0 }}
                maxBarSize={weekly ? 28 : 48}
              >
                <defs>
                  <pattern
                    id={hatchId}
                    width={6}
                    height={6}
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(45)"
                  >
                    <rect width={6} height={6} fill={c.inProgress.ground} />
                    <line x1={0} y1={0} x2={0} y2={6} stroke={c.inProgress.stripe} strokeWidth={2.5} />
                  </pattern>
                </defs>

                <CartesianGrid vertical={false} stroke={c.grid} />

                <XAxis
                  dataKey="key"
                  tickLine={false}
                  axisLine={false}
                  height={40}
                  interval={tickInterval}
                  tick={(props: XAxisTickContentProps) => <CycleTick {...props} rows={rows} />}
                />
                <YAxis
                  allowDecimals={false}
                  domain={[0, yScale.max]}
                  ticks={yScale.ticks}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                />

                {/* isAnimationActive: the wrapper the card measures itself against has to jump to the
                    cursor, not glide there. See ChartHoverCard. */}
                <RechartsTooltip cursor={false} isAnimationActive={false} content={<CycleTooltip weekly={weekly} />} />

                {/* One stacked series per cycle week, bottom first. The white stroke separates the weeks of
                    a cycle; a single-week cycle has nothing to separate and keeps its rounded top. */}
                {Array.from({ length: segmentCount }, (_, index) => (
                  <Bar
                    key={index}
                    dataKey={(row: ChartRow) => row.segments[index] ?? 0}
                    stackId="cycle"
                    radius={weekly ? [4, 4, 0, 0] : 0}
                    isAnimationActive={animateFirstDraw}
                    animationDuration={CHART_GROW_MS}
                    animationEasing="ease-out"
                  >
                    {rows.map((row) => (
                      <Cell
                        key={row.key}
                        fill={segmentFill(row, index)}
                        stroke={weekly ? undefined : '#ffffff'}
                        strokeWidth={weekly ? 0 : 1.5}
                      />
                    ))}
                  </Bar>
                ))}

                {/* The in-progress cycle's way still to go: an outline only, so it reads as a target rather
                    than as piglets. Completed cycles carry zero here and draw nothing. */}
                <Bar dataKey="remaining" stackId="cycle" isAnimationActive={false}>
                  {rows.map((row) => (
                    <Cell
                      key={row.key}
                      fill="transparent"
                      stroke={row.remaining > 0 ? c.inProgress.stripe : 'none'}
                      strokeDasharray="3 3"
                    />
                  ))}
                </Bar>

                {/* An invisible series that only carries the totals, so each label sits on top of the whole
                    stack - or of the outline - instead of on whichever week happens to be the top segment. */}
                <Line
                  dataKey="labelY"
                  stroke="none"
                  dot={false}
                  activeDot={false}
                  legendType="none"
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey="totalLabel"
                    position="top"
                    offset={8}
                    fill={c.ink}
                    fontSize={11}
                    fontWeight={600}
                  />
                </Line>

                {expectedPerCycle > 0 && (
                  <ReferenceLine
                    y={expectedPerCycle}
                    stroke={c.expected}
                    strokeWidth={2}
                    strokeDasharray="6 6"
                    ifOverflow="extendDomain"
                    label={{
                      value: t(weekly ? 'f-piglets-reg-expected-line' : 'f-piglets-reg-expected-line-cycle', {
                        value: num(expectedPerCycle)
                      }),
                      position: 'right',
                      fill: '#6b7280',
                      fontSize: 11
                    }}
                  />
                )}
              </ComposedChart>
            </ChartContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
